import clsx from 'clsx'
import React from 'react'
import shallow from 'zustand/shallow'

import { Icon } from '@wartek-id/icon'
import { Shimmer } from '@wartek-id/shimmer'
import { Text } from '@wartek-id/text'

import { getFirstAndLastItem } from 'utils/array'
import { SLB_DETAIL } from '../../../data'
import { useToolkitFilterStore } from 'stores/toolkitFilters'

import type { TSLBPhaseDetail } from '../../../data'
import type { TFase, TFaseGroup } from 'types/classAndPhases'

export type TPhaseGroupDetail =
  | Record<TFase, string[]>
  | Record<string, string[]>
  | Record<TFase, TSLBPhaseDetail>
export type TPhaseGroupMapping = Record<TFaseGroup, TPhaseGroupDetail>

interface PhaseListProps {
  activeFaseGroup: TFaseGroup
  selectedFase: TFase
  setSelectedFase: (fase: TFase) => void
}

const FaseList = ({
  activeFaseGroup,
  selectedFase,
  setSelectedFase,
}: PhaseListProps) => {
  const { isLoading: isFiltersDataLoading, phaseClassList } =
    useToolkitFilterStore(
      ({ isLoading, phaseClassList }) => ({ isLoading, phaseClassList }),
      shallow
    )

  const helperTextColor = (fase) =>
    selectedFase && selectedFase === fase ? 'primary' : 'subdued'

  const phaseGroupMapping: TPhaseGroupMapping = {
    Umum: phaseClassList,
    SMK: {
      E: ['10 SMK'],
      F: ['11', '12 SMK'],
    },
    SLB: SLB_DETAIL,
  }

  const selectFase = (fase: TFase) => {
    setSelectedFase(selectedFase === fase ? null : fase)
  }

  if (
    (!phaseClassList || Object?.keys(phaseClassList)?.length === 0) &&
    isFiltersDataLoading
  ) {
    return (
      <>
        {Array.from({ length: 7 })?.map((_, index) => (
          <Shimmer height={30} className="mt-4 !w-full" key={index} />
        ))}
      </>
    )
  }

  return (
    <ul className="list-type-none mt-7 mb-[64px]">
      {activeFaseGroup === 'SLB'
        ? Object?.entries(phaseGroupMapping?.[activeFaseGroup] ?? {}).map(
            ([fase, detail]: [TFase, TSLBPhaseDetail]) => (
              <li
                className="flex cursor-pointer justify-between border-b border-disabled py-3"
                onClick={() => selectFase(fase)}
                key={fase}
              >
                <span>
                  <div className="flex">
                    <Text color={helperTextColor(fase)}>Fase {fase} · </Text>
                    <Text color={helperTextColor(fase)}>
                      &nbsp; Kelas{' '}
                      {detail?.classes.map((klass, i) => (
                        <span className="text-inherit" key={klass}>
                          {klass.replace('Kelas', '')}{' '}
                          {i === 0 && detail?.classes.length > 1 && '-'}
                        </span>
                      ))}
                    </Text>
                  </div>
                  {detail?.mentalAge && (
                    <Text
                      className="!text-[13px]"
                      color={helperTextColor(fase)}
                    >
                      Usia mental {detail?.mentalAge} tahun
                    </Text>
                  )}
                </span>
                {selectedFase === fase && (
                  <Icon color="informational">check</Icon>
                )}
              </li>
            )
          )
        : Object?.entries(phaseGroupMapping?.[activeFaseGroup] ?? {}).map(
            ([fase, classes]: [TFase, string[]]) => (
              <li
                className={clsx('cursor-pointer border-b border-disabled py-3')}
                onClick={() => selectFase(fase)}
                key={fase}
              >
                <div className="flex justify-between">
                  <span className="flex">
                    <Text color={helperTextColor(fase)}>
                      Fase {fase}&nbsp; ·{' '}
                    </Text>
                    <Text color={helperTextColor(fase)}>
                      &nbsp; Kelas{' '}
                      {getFirstAndLastItem(classes)?.map((klass, i) => (
                        <span className="text-inherit" key={klass}>
                          {klass.replace('Kelas', '')}{' '}
                          {i === 0 && classes.length > 1 && '-'}
                        </span>
                      ))}
                    </Text>
                  </span>
                  {selectedFase === fase && (
                    <Icon color="informational">check</Icon>
                  )}
                </div>
              </li>
            )
          )}
    </ul>
  )
}

export default FaseList
